import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Wand2, Sparkles, Loader2, X } from "lucide-react";

/**
 * MagicWand — golden breathing glow when the AI is confident (Vision/Pinterest data),
 * click triggers a sparkle micro-animation and an elegant chat-bubble overlay with
 * 3 personalized message ideas. Picking one populates the field via onPick.
 */
const MagicWand = ({ lovedOneId, occasion = "Birthday", tone = "warm and heartfelt", highConfidence = false, glowNote = "", onPick }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState("");
  const [popping, setPopping] = useState(false);
  const btnRef = useRef(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.post(`/loved-ones/${lovedOneId}/message-suggestions`, { occasion, tone });
      setSuggestions(res.data.suggestions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't fetch ideas.");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setPopping(true);
    setTimeout(() => setPopping(false), 650);
    const next = !open;
    setOpen(next);
    if (next && suggestions.length === 0) fetchSuggestions();
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        data-testid="magic-wand"
        onClick={handleClick}
        title="Magic Wand — AI message ideas"
        className={`relative w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
          highConfidence
            ? "wand-glow border-yellow-500/40 bg-yellow-500/10"
            : "border-border bg-muted/50 text-primary hover:bg-muted"
        }`}
      >
        <Wand2 className={`w-4 h-4 ${popping ? "wand-pop" : ""}`} />
        {popping && (
          <>
            <Sparkles className="sparkle-burst absolute w-4 h-4 gold-text -top-1 -right-1" />
            <Sparkles className="sparkle-burst absolute w-3 h-3 gold-text -bottom-1 -left-1" />
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="wand-bubble"
          className="absolute right-0 z-40 mt-2 w-80 surface rounded-3xl p-4 bubble-in"
          style={{ top: "100%" }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 gold-text" /> Message ideas
            </span>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-muted/60 text-muted-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          {highConfidence && (
            <div className="text-[11px] gold-text mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {glowNote || "Tuned to their visual taste"}
            </div>
          )}

          {loading ? (
            <div className="py-6 flex items-center justify-center text-muted-foreground text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Composing ideas…
            </div>
          ) : error ? (
            <div className="text-sm text-red-500 py-3">{error}</div>
          ) : (
            <div className="space-y-2" data-testid="wand-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`wand-suggestion-${i}`}
                  onClick={() => { onPick?.(s); setOpen(false); }}
                  className="w-full text-left text-sm bg-muted/40 hover:bg-primary/10 border border-transparent hover:border-primary/30 rounded-2xl px-3.5 py-3 leading-relaxed font-serif transition-colors"
                >
                  {s}
                </button>
              ))}
              <button onClick={fetchSuggestions} className="w-full text-xs text-muted-foreground hover:text-foreground py-1">
                ↻ New ideas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MagicWand;
